import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc, increment, orderBy } from 'firebase/firestore';
import { Star, ThumbsUp, ChevronLeft, ChevronRight, SortAsc, MessageSquare, Image as ImageIcon } from 'lucide-react';

function ProductReviewList({ productId }) {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('latest'); // latest, ratingHigh, ratingLow
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedImage, setSelectedImage] = useState(null);
    const [helpfulClicked, setHelpfulClicked] = useState({});
    const REVIEWS_PER_PAGE = 10;

    const loadReviews = useCallback(async () => {
        if (!productId) return;
        setLoading(true);

        try {
            const reviewsRef = collection(db, 'productReviews');
            const reviewsQuery = query(
                reviewsRef,
                where('productId', '==', productId),
                where('status', '==', 'active'),
                orderBy('createdAt', 'desc')
            );
            const snapshot = await getDocs(reviewsQuery);
            const reviewsList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setReviews(reviewsList);
        } catch (err) {
            console.error('리뷰 로드 실패:', err);
        } finally {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        loadReviews();
    }, [loadReviews]);

    // Sorting
    const sortedReviews = [...reviews].sort((a, b) => {
        if (sortBy === 'ratingHigh') return b.rating - a.rating;
        if (sortBy === 'ratingLow') return a.rating - b.rating;
        // latest
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime();
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime();
        return bTime - aTime;
    });

    // Pagination
    const totalPages = Math.ceil(sortedReviews.length / REVIEWS_PER_PAGE);
    const paginatedReviews = sortedReviews.slice(
        (currentPage - 1) * REVIEWS_PER_PAGE,
        currentPage * REVIEWS_PER_PAGE
    );

    // Statistics
    const totalReviews = reviews.length;
    const averageRating = totalReviews > 0
        ? (reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews).toFixed(1)
        : 0;

    const ratingDistribution = [5, 4, 3, 2, 1].map(star => ({
        star,
        count: reviews.filter(r => r.rating === star).length,
        percentage: totalReviews > 0
            ? (reviews.filter(r => r.rating === star).length / totalReviews) * 100
            : 0
    }));

    const handleHelpful = async (reviewId) => {
        if (helpfulClicked[reviewId]) return;

        try {
            const reviewRef = doc(db, 'productReviews', reviewId);
            await updateDoc(reviewRef, {
                helpfulCount: increment(1)
            });
            setHelpfulClicked(prev => ({ ...prev, [reviewId]: true }));
            setReviews(prev => prev.map(r =>
                r.id === reviewId ? { ...r, helpfulCount: (r.helpfulCount || 0) + 1 } : r
            ));
        } catch (err) {
            console.error('도움이 됐어요 업데이트 실패:', err);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const renderStars = (rating, size = 16) => {
        return (
            <div style={{ display: 'flex', gap: '2px' }}>
                {[1, 2, 3, 4, 5].map(star => (
                    <Star
                        key={star}
                        size={size}
                        color={star <= rating ? '#f59e0b' : '#e2e8f0'}
                        fill={star <= rating ? '#f59e0b' : 'none'}
                    />
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p style={styles.loadingText}>리뷰를 불러오는 중...</p>
                <style>{`
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Rating Summary */}
            <div style={styles.summarySection}>
                <div style={styles.summaryLeft}>
                    <div style={styles.averageRatingValue}>{averageRating}</div>
                    {renderStars(Math.round(averageRating), 24)}
                    <p style={styles.totalReviewsText}>총 {totalReviews}개 리뷰</p>
                </div>
                <div style={styles.summaryRight}>
                    {ratingDistribution.map(({ star, count, percentage }) => (
                        <div key={star} style={styles.distributionRow}>
                            <span style={styles.distributionStar}>{star}점</span>
                            <div style={styles.distributionBarBg}>
                                <div
                                    style={{
                                        ...styles.distributionBarFill,
                                        width: `${percentage}%`
                                    }}
                                />
                            </div>
                            <span style={styles.distributionCount}>{count}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Sort & Count */}
            <div style={styles.sortBar}>
                <div style={styles.sortInfo}>
                    <MessageSquare size={18} color="#6366f1" />
                    <span style={styles.sortInfoText}>리뷰 {totalReviews}개</span>
                </div>
                <div style={styles.sortOptions}>
                    {[
                        { key: 'latest', label: '최신순' },
                        { key: 'ratingHigh', label: '평점 높은순' },
                        { key: 'ratingLow', label: '평점 낮은순' }
                    ].map(option => (
                        <button
                            key={option.key}
                            style={{
                                ...styles.sortButton,
                                ...(sortBy === option.key ? styles.sortButtonActive : {})
                            }}
                            onClick={() => { setSortBy(option.key); setCurrentPage(1); }}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Reviews List */}
            {totalReviews === 0 ? (
                <div style={styles.emptyContainer}>
                    <Star size={64} color="#e2e8f0" />
                    <p style={styles.emptyTitle}>아직 리뷰가 없습니다</p>
                    <p style={styles.emptySubtitle}>첫 번째 리뷰를 작성해보세요!</p>
                </div>
            ) : (
                <div style={styles.reviewsList}>
                    {paginatedReviews.map((review) => (
                        <div key={review.id} style={styles.reviewCard}>
                            <div style={styles.reviewHeader}>
                                <div style={styles.reviewHeaderLeft}>
                                    {renderStars(review.rating)}
                                    {review.isVerifiedPurchase && (
                                        <span style={styles.verifiedBadge}>구매 인증</span>
                                    )}
                                </div>
                                <span style={styles.reviewDate}>{formatDate(review.createdAt)}</span>
                            </div>

                            <div style={styles.reviewerName}>{review.buyerName || '익명'}</div>
                            <h4 style={styles.reviewTitle}>{review.title}</h4>
                            <p style={styles.reviewContent}>{review.content}</p>

                            {/* Review Images */}
                            {review.images && review.images.length > 0 && (
                                <div style={styles.reviewImages}>
                                    {review.images.map((img, idx) => (
                                        <img
                                            key={idx}
                                            src={img}
                                            alt={`리뷰 이미지 ${idx + 1}`}
                                            style={styles.reviewThumbnail}
                                            onClick={() => setSelectedImage(img)}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Helpful Button */}
                            <div style={styles.reviewFooter}>
                                <button
                                    style={{
                                        ...styles.helpfulBtn,
                                        ...(helpfulClicked[review.id] ? styles.helpfulBtnClicked : {})
                                    }}
                                    onClick={() => handleHelpful(review.id)}
                                    disabled={helpfulClicked[review.id]}
                                >
                                    <ThumbsUp size={16} />
                                    <span>도움이 됐어요 {review.helpfulCount > 0 ? `(${review.helpfulCount})` : ''}</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={styles.pagination}>
                    <button
                        style={{
                            ...styles.pageBtn,
                            ...(currentPage === 1 ? styles.pageBtnDisabled : {})
                        }}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                    >
                        <ChevronLeft size={18} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                            key={page}
                            style={{
                                ...styles.pageBtn,
                                ...(currentPage === page ? styles.pageBtnActive : {})
                            }}
                            onClick={() => setCurrentPage(page)}
                        >
                            {page}
                        </button>
                    ))}
                    <button
                        style={{
                            ...styles.pageBtn,
                            ...(currentPage === totalPages ? styles.pageBtnDisabled : {})
                        }}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
            )}

            {/* Full Image Modal */}
            {selectedImage && (
                <div style={styles.imageOverlay} onClick={() => setSelectedImage(null)}>
                    <img src={selectedImage} alt="리뷰 이미지" style={styles.fullImage} />
                </div>
            )}

            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
}

const styles = {
    container: {
        width: '100%'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px'
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '3px solid #e2e8f0',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    loadingText: {
        marginTop: '16px',
        fontSize: '14px',
        color: '#64748b'
    },

    // Summary
    summarySection: {
        display: 'flex',
        gap: '32px',
        padding: '24px',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginBottom: '20px',
        flexWrap: 'wrap'
    },
    summaryLeft: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '120px',
        padding: '16px'
    },
    averageRatingValue: {
        fontSize: '48px',
        fontWeight: '800',
        color: '#0f172a',
        lineHeight: 1
    },
    totalReviewsText: {
        marginTop: '8px',
        fontSize: '14px',
        color: '#64748b'
    },
    summaryRight: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        justifyContent: 'center',
        minWidth: '200px'
    },
    distributionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    distributionStar: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        width: '30px',
        textAlign: 'right',
        flexShrink: 0
    },
    distributionBarBg: {
        flex: 1,
        height: '10px',
        backgroundColor: '#f1f5f9',
        borderRadius: '5px',
        overflow: 'hidden'
    },
    distributionBarFill: {
        height: '100%',
        backgroundColor: '#f59e0b',
        borderRadius: '5px',
        transition: 'width 0.3s ease'
    },
    distributionCount: {
        fontSize: '13px',
        color: '#94a3b8',
        width: '24px',
        textAlign: 'right',
        flexShrink: 0
    },

    // Sort Bar
    sortBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 0',
        borderBottom: '1px solid #e2e8f0',
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: '12px'
    },
    sortInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    sortInfoText: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1e293b'
    },
    sortOptions: {
        display: 'flex',
        gap: '8px'
    },
    sortButton: {
        padding: '6px 14px',
        fontSize: '13px',
        fontWeight: '500',
        color: '#64748b',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    sortButtonActive: {
        color: '#ffffff',
        backgroundColor: '#6366f1'
    },

    // Empty
    emptyContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px'
    },
    emptyTitle: {
        marginTop: '16px',
        fontSize: '18px',
        fontWeight: '600',
        color: '#64748b'
    },
    emptySubtitle: {
        marginTop: '4px',
        fontSize: '14px',
        color: '#94a3b8'
    },

    // Review Cards
    reviewsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
    },
    reviewCard: {
        padding: '20px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #f1f5f9',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    reviewHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        flexWrap: 'wrap',
        gap: '8px'
    },
    reviewHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    verifiedBadge: {
        fontSize: '11px',
        fontWeight: '600',
        color: '#10b981',
        backgroundColor: '#d1fae5',
        padding: '2px 8px',
        borderRadius: '6px'
    },
    reviewDate: {
        fontSize: '13px',
        color: '#94a3b8'
    },
    reviewerName: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#475569',
        marginBottom: '8px'
    },
    reviewTitle: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#0f172a',
        margin: '0 0 8px 0'
    },
    reviewContent: {
        fontSize: '14px',
        color: '#475569',
        lineHeight: '1.7',
        margin: '0 0 12px 0',
        whiteSpace: 'pre-wrap'
    },
    reviewImages: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        marginBottom: '12px'
    },
    reviewThumbnail: {
        width: '72px',
        height: '72px',
        objectFit: 'cover',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        cursor: 'pointer',
        transition: 'opacity 0.2s'
    },
    reviewFooter: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: '12px',
        borderTop: '1px solid #f1f5f9'
    },
    helpfulBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 14px',
        fontSize: '13px',
        fontWeight: '500',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    helpfulBtnClicked: {
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        borderColor: '#c7d2fe'
    },

    // Pagination
    pagination: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
        marginTop: '24px',
        padding: '16px 0'
    },
    pageBtn: {
        minWidth: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 8px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#64748b',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    pageBtnActive: {
        color: '#ffffff',
        backgroundColor: '#6366f1',
        borderColor: '#6366f1'
    },
    pageBtnDisabled: {
        color: '#cbd5e1',
        cursor: 'not-allowed',
        opacity: 0.5
    },

    // Image Overlay
    imageOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        cursor: 'pointer',
        padding: '40px'
    },
    fullImage: {
        maxWidth: '90%',
        maxHeight: '90%',
        objectFit: 'contain',
        borderRadius: '8px'
    }
};

export default ProductReviewList;
